import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Form } from "react-bootstrap";
import { Task, TimeEntry, Project } from "../../types/Work";

interface TaskDetailsModalProps {
  show: boolean;
  onHide: () => void;
  task: Task | null;
  projects: Project[];
  onStartTimer: (taskId: string) => void;
  onStopTimer: (taskId: string) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onAddTimeEntry: (taskId: string, entry: Omit<TimeEntry, "id">) => void;
  onUpdateTimeEntry: (
    taskId: string,
    entryId: string,
    updates: Partial<TimeEntry>
  ) => void;
  onDeleteTimeEntry: (taskId: string, entryId: string) => void;
}

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  show,
  onHide,
  task,
  projects,
  onStartTimer,
  onStopTimer,
  onEditTask,
  onDeleteTask,
  onUpdateTask,
  onAddTimeEntry,
  onUpdateTimeEntry,
  onDeleteTimeEntry,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<Partial<TimeEntry>>({});
  const [editingDurationInput, setEditingDurationInput] = useState("");

  useEffect(() => {
    if (!task?.isTimerRunning || !task?.currentTimerStart) {
      setElapsedSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const start = new Date(task.currentTimerStart!);
      const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
      setElapsedSeconds(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [task?.isTimerRunning, task?.currentTimerStart]);

  if (!task) return null;

  const project = task.projectId
    ? projects.find((p) => p.id === task.projectId)
    : null;

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDurationInput = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const parseDurationInput = (value: string): number | null => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;

    const match = normalized.match(/^(?:(\d+(?:\.\d+)?)h)?\s*(?:(\d+)m)?$/);
    if (!match || !(match[1] || match[2])) {
      return null;
    }

    const hours = match[1] ? parseFloat(match[1]) : 0;
    const minutes = match[2] ? parseInt(match[2]) : 0;

    return Math.round(hours * 60 + minutes);
  };

  const formatTimer = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  const getTotalTime = () => {
    return task.timeEntries.reduce(
      (sum, entry) => sum + entry.durationMinutes,
      0
    );
  };

  const formatDateTimeLocalInput = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-danger";
      case "high":
        return "bg-warning";
      case "medium":
        return "bg-info";
      case "low":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  const startEditingEntry = (entry: TimeEntry) => {
    setEditingEntryId(entry.id);
    setEditingEntry({
      startAt: entry.startAt,
      endAt: entry.endAt,
      durationMinutes: entry.durationMinutes,
      note: entry.note,
    });
    setEditingDurationInput(formatDurationInput(entry.durationMinutes));
  };

  const saveEditingEntry = () => {
    if (!editingEntryId || !task) return;

    let updates = { ...editingEntry };

    // Recalculate duration if dates changed
    if (updates.startAt && updates.endAt) {
      const start = new Date(updates.startAt).getTime();
      const end = new Date(updates.endAt).getTime();
      updates.durationMinutes = Math.floor((end - start) / 1000 / 60);
    }

    onUpdateTimeEntry(task.id, editingEntryId, updates);
    setEditingEntryId(null);
    setEditingEntry({});
    setEditingDurationInput("");
  };

  const cancelEditingEntry = () => {
    setEditingEntryId(null);
    setEditingEntry({});
    setEditingDurationInput("");
  };

  const toggleSubtask = (subtaskId: string) => {
    if (!task) return;

    const updatedSubtasks = task.subtasks.map((s) =>
      s.id === subtaskId ? { ...s, done: !s.done } : s
    );

    onUpdateTask(task.id, { subtasks: updatedSubtasks });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {task.name}
          <Badge className={getPriorityBadgeClass(task.priority)}>
            {task.priority}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Project Info */}
        {project && (
          <div className="mb-3">
            <small className="text-muted">Project:</small>
            <div>
              <Badge style={{ backgroundColor: project.color }}>
                {project.name}
              </Badge>
            </div>
          </div>
        )}

        {/* Description */}
        {task.descriptionHtml && (
          <div className="mb-3">
            <h6>Description</h6>
            <div dangerouslySetInnerHTML={{ __html: task.descriptionHtml }} />
          </div>
        )}

        {/* Subtasks */}
        {task.subtasks.length > 0 && (
          <div className="mb-3">
            <h6>
              Subtasks ({task.subtasks.filter((s) => s.done).length}/
              {task.subtasks.length})
            </h6>
            {task.subtasks.map((subtask) => (
              <div key={subtask.id} className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  checked={subtask.done}
                  onChange={() => toggleSubtask(subtask.id)}
                />
                <span
                  style={{
                    textDecoration: subtask.done ? "line-through" : "none",
                  }}
                >
                  {subtask.title}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Timer */}
        <div className="mb-3">
          <h6>Timer</h6>
          <div className="d-flex align-items-center gap-3">
            {task.isTimerRunning ? (
              <>
                <div className="fs-3 font-monospace text-danger">
                  {formatTimer(elapsedSeconds)}
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onStopTimer(task.id)}
                >
                  <i className="fas fa-stop me-1"></i>
                  Stop
                </Button>
              </>
            ) : (
              <Button
                variant="success"
                size="sm"
                onClick={() => onStartTimer(task.id)}
              >
                <i className="fas fa-play me-1"></i>
                Start Timer
              </Button>
            )}
          </div>
        </div>

        {/* Time Entries */}
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0">Time Entries</h6>
            {getTotalTime() > 0 && (
              <Badge bg="primary" className="px-3 py-2">
                Total: {formatDuration(getTotalTime())}
              </Badge>
            )}
          </div>
          {task.timeEntries.length === 0 ? (
            <div className="text-center py-4">
              <i
                className="fas fa-clock-rotate-left text-muted mb-2"
                style={{ fontSize: "2rem", opacity: 0.3 }}
              ></i>
              <p className="text-muted mb-0">No time entries yet</p>
              <small className="text-muted">
                Start the timer to track your work
              </small>
            </div>
          ) : (
            <div className="time-entries-list">
              {task.timeEntries.map((entry) => (
                <div key={entry.id} className="time-entry-item">
                  {editingEntryId === entry.id ? (
                    <div className="time-entry-edit">
                      <div className="row g-2">
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label className="small fw-semibold text-muted">
                              Start
                            </Form.Label>
                            <Form.Control
                              type="datetime-local"
                              size="sm"
                              value={
                                editingEntry.startAt
                                  ? formatDateTimeLocalInput(
                                      new Date(editingEntry.startAt),
                                    )
                                  : formatDateTimeLocalInput(
                                      new Date(entry.startAt),
                                    )
                              }
                              onChange={(e) => {
                                const newStart = new Date(e.target.value);
                                const durationMinutes =
                                  editingEntry.durationMinutes ??
                                  entry.durationMinutes;
                                const nextEnd =
                                  durationMinutes !== undefined
                                    ? new Date(
                                        newStart.getTime() +
                                          durationMinutes * 60000,
                                      )
                                    : (editingEntry.endAt ?? entry.endAt);

                                setEditingEntry({
                                  ...editingEntry,
                                  startAt: newStart,
                                  endAt: nextEnd,
                                });
                              }}
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label className="small fw-semibold text-muted">
                              End
                            </Form.Label>
                            <Form.Control
                              type="datetime-local"
                              size="sm"
                              value={
                                editingEntry.endAt !== undefined
                                  ? editingEntry.endAt
                                    ? formatDateTimeLocalInput(
                                        new Date(editingEntry.endAt),
                                      )
                                    : ""
                                  : entry.endAt
                                    ? formatDateTimeLocalInput(
                                        new Date(entry.endAt),
                                      )
                                    : ""
                              }
                              onChange={(e) => {
                                const value = e.target.value;

                                if (!value) {
                                  setEditingEntry({
                                    ...editingEntry,
                                    endAt: undefined,
                                  });
                                  return;
                                }

                                const startDate =
                                  editingEntry.startAt || entry.startAt;
                                const newEnd = new Date(value);
                                const durationMinutes = Math.floor(
                                  (newEnd.getTime() - startDate.getTime()) /
                                    60000,
                                );

                                setEditingEntry({
                                  ...editingEntry,
                                  endAt: newEnd,
                                  durationMinutes,
                                });
                                setEditingDurationInput(
                                  formatDurationInput(durationMinutes),
                                );
                              }}
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-4">
                          <Form.Group>
                            <Form.Label className="small fw-semibold text-muted">
                              Duration
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="e.g. 1h30m"
                              value={editingDurationInput}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEditingDurationInput(value);
                                const minutes = parseDurationInput(value);
                                if (minutes !== null) {
                                  const startDate =
                                    editingEntry.startAt || entry.startAt;
                                  const nextEnd = new Date(startDate);
                                  nextEnd.setMinutes(
                                    nextEnd.getMinutes() + minutes,
                                  );
                                  setEditingEntry({
                                    ...editingEntry,
                                    durationMinutes: minutes,
                                    endAt: nextEnd,
                                  });
                                }
                              }}
                            />
                          </Form.Group>
                        </div>
                        <div className="col-12">
                          <Form.Group>
                            <Form.Label className="small fw-semibold text-muted">
                              Note
                            </Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Add a note..."
                              value={
                                editingEntry.note !== undefined
                                  ? editingEntry.note || ""
                                  : entry.note || ""
                              }
                              onChange={(e) =>
                                setEditingEntry({
                                  ...editingEntry,
                                  note: e.target.value,
                                })
                              }
                            />
                          </Form.Group>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={saveEditingEntry}
                        >
                          <i className="fas fa-check me-1"></i>
                          Save
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={cancelEditingEntry}
                        >
                          <i className="fas fa-xmark me-1"></i>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="time-entry-display">
                      <div className="time-entry-header">
                        <div className="time-entry-duration">
                          <i className="fas fa-clock me-2 text-primary"></i>
                          <span className="fw-bold">
                            {formatDuration(entry.durationMinutes)}
                          </span>
                        </div>
                        <div className="time-entry-actions">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-primary"
                            onClick={() => startEditingEntry(entry)}
                            title="Edit"
                          >
                            <i className="fas fa-pencil"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this time entry?",
                                )
                              ) {
                                onDeleteTimeEntry(task.id, entry.id);
                              }
                            }}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </div>
                      <div className="time-entry-dates">
                        <small className="text-muted">
                          <i className="fas fa-calendar-day me-1"></i>
                          {formatDate(entry.startAt)}
                          {entry.endAt && (
                            <>
                              <i className="fas fa-arrow-right mx-2"></i>
                              {formatDate(entry.endAt)}
                            </>
                          )}
                          {!entry.endAt && (
                            <Badge bg="success" className="ms-2">
                              <i
                                className="fas fa-circle me-1"
                                style={{ fontSize: "0.5rem" }}
                              ></i>
                              Running
                            </Badge>
                          )}
                        </small>
                      </div>
                      {entry.note && (
                        <div className="time-entry-note">
                          <i className="fas fa-note-sticky me-2 text-muted"></i>
                          <small>{entry.note}</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Task Info */}
        <div className="mt-3 pt-0">
          <small className="text-muted">
            Created: {formatDate(task.createdAt)} • Updated:{" "}
            {formatDate(task.updatedAt)}
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="danger"
          onClick={() => {
            if (window.confirm("Are you sure you want to delete this task?")) {
              onDeleteTask(task.id);
              onHide();
            }
          }}
        >
          Delete Task
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={() => onEditTask(task)}>
          Edit Task
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
